import React, { useEffect, useState, useCallback } from 'react';
import { UserPlus, X, Trash, Shield } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { SpotPicker } from '../entry/SpotPicker';

/**
 * Producer-only panel to reserve stalls / RV spots for staff or workers.
 * Blocks the spots out on the public map exactly like a paid rider reservation.
 */
export const StaffReservations = ({ showId }) => {
  const [hasChart, setHasChart] = useState(false);
  const [staffReservations, setStaffReservations] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [staffName, setStaffName] = useState('');
  const [notes, setNotes] = useState('');
  const [selection, setSelection] = useState({ spots: [] });
  const [submitting, setSubmitting] = useState(false);
  const [loadKey, setLoadKey] = useState(0);

  const loadData = useCallback(async () => {
    try {
      const [mapRes, resRes] = await Promise.all([
        api.get(`/shows/${showId}/spot-map`),
        api.get(`/shows/${showId}/spot-reservations`),
      ]);
      setHasChart(!!(mapRes.data?.has_stall_chart || mapRes.data?.has_rv_chart));
      const staff = (resRes.data || []).filter(r => r.reserved_by_producer);
      setStaffReservations(staff);
    } catch {
      // Non-fatal — panel just stays hidden if producer has no chart
    }
  }, [showId]);

  useEffect(() => { loadData(); }, [loadData, loadKey]);

  const submit = async () => {
    if (!staffName.trim()) {
      toast.error('Please enter a staff name');
      return;
    }
    if (!selection.spots?.length) {
      toast.error('Select at least one spot on the map');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/shows/${showId}/reserve-spots-staff`, {
        spots: selection.spots,
        staff_name: staffName.trim(),
        notes: notes.trim(),
      });
      const created = data.count || 0;
      const skipped = (data.skipped || []).length;
      if (created > 0) toast.success(`Reserved ${created} spot${created !== 1 ? 's' : ''} for ${staffName.trim()}`);
      if (skipped > 0) toast.warning(`${skipped} spot${skipped !== 1 ? 's were' : ' was'} already taken and skipped`);
      setModalOpen(false);
      setStaffName('');
      setNotes('');
      setSelection({ spots: [] });
      setLoadKey(k => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reserve spots');
    } finally {
      setSubmitting(false);
    }
  };

  const release = async (reservationId, label) => {
    if (!window.confirm(`Release ${label}? The spot will become available again.`)) return;
    try {
      await api.delete(`/spot-reservations/${reservationId}`);
      toast.success('Spot released');
      setLoadKey(k => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to release spot');
    }
  };

  // Group staff reservations by person
  const grouped = staffReservations.reduce((acc, r) => {
    const key = `${r.rider_name}||${r.staff_notes || ''}`;
    if (!acc[key]) acc[key] = { name: r.rider_name, notes: r.staff_notes, items: [] };
    acc[key].items.push(r);
    return acc;
  }, {});
  const groupedList = Object.values(grouped);

  if (!hasChart) return null;

  return (
    <div
      className="mt-8 bg-[#161616] border border-[#262626] rounded-lg p-6"
      data-testid="staff-reservations-section"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Shield size={22} className="text-[#FF9500]" />
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>
            Staff Reservations
            <span className="text-[#9E9E9E] text-sm font-normal ml-2">
              ({staffReservations.length} spot{staffReservations.length !== 1 ? 's' : ''})
            </span>
          </h3>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#FF9500] hover:bg-[#FF9500]/90 text-white text-sm font-bold rounded-md transition-colors"
          data-testid="staff-reservation-open-btn"
        >
          <UserPlus size={16} weight="bold" /> Reserve for Staff
        </button>
      </div>

      {staffReservations.length === 0 ? (
        <p className="text-[#9E9E9E] text-sm">
          Block out stalls or RV spots for your workers here. They'll show as unavailable on the public map exactly like a paid rider reservation.
        </p>
      ) : (
        <div className="space-y-3">
          {groupedList.map((g, gi) => (
            <div key={gi} className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3" data-testid={`staff-group-${gi}`}>
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div>
                  <p className="text-white font-semibold text-sm">{g.name}</p>
                  {g.notes && <p className="text-[#9E9E9E] text-xs mt-0.5">{g.notes}</p>}
                </div>
                <span className="text-[#FF9500] text-xs font-bold bg-[#FF9500]/10 border border-[#FF9500]/30 px-2 py-0.5 rounded-full">
                  {g.items.length} spot{g.items.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {g.items.map((r) => {
                  const label = `${r.type === 'stall' ? 'Stall' : 'RV'} ${r.barn} ${r.row}-${r.spot_number}`;
                  return (
                    <span
                      key={r.id}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-[#1a1a1a] border border-[#333] rounded text-xs text-white"
                      data-testid={`staff-spot-${r.id}`}
                    >
                      {label}
                      <button
                        onClick={() => release(r.id, label)}
                        className="ml-1 text-[#9E9E9E] hover:text-[#FF3B30] transition-colors"
                        title="Release spot"
                        data-testid={`staff-release-${r.id}`}
                      >
                        <Trash size={12} weight="bold" />
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3"
          onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
        >
          <div className="bg-[#0F0F0F] border border-[#262626] rounded-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="staff-reservation-modal">
            <div className="sticky top-0 bg-[#0F0F0F] border-b border-[#262626] px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <UserPlus size={20} className="text-[#FF9500]" /> Reserve Spots for Staff
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="text-[#9E9E9E] hover:text-white transition-colors"
                data-testid="staff-reservation-close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#9E9E9E] font-semibold mb-1">Staff / Worker Name *</label>
                  <input
                    value={staffName}
                    onChange={(e) => setStaffName(e.target.value)}
                    placeholder="e.g. John Smith"
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:border-[#FF9500] focus:outline-none"
                    data-testid="staff-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#9E9E9E] font-semibold mb-1">Notes (optional)</label>
                  <input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Barn Manager"
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:border-[#FF9500] focus:outline-none"
                    data-testid="staff-notes-input"
                  />
                </div>
              </div>

              <div>
                <p className="text-xs text-[#9E9E9E] font-semibold mb-2">Select spots on the map</p>
                <SpotPicker showId={showId} onSelectionChange={setSelection} staffMode />
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-[#262626]">
                <p className="text-sm text-[#9E9E9E]" data-testid="staff-selection-summary">
                  {selection.spots?.length || 0} spot{(selection.spots?.length || 0) !== 1 ? 's' : ''} selected
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setModalOpen(false)}
                    className="px-4 py-2 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] text-sm font-semibold rounded-md hover:text-white"
                    data-testid="staff-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submit}
                    disabled={submitting || !staffName.trim() || !(selection.spots?.length)}
                    className="px-5 py-2 bg-[#FF9500] hover:bg-[#FF9500]/90 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-md transition-colors"
                    data-testid="staff-submit-btn"
                  >
                    {submitting ? 'Reserving…' : 'Block Spots'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
